import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { api, type ObjectRecord } from '../api';
import { findObjectType } from '../objectTypes';

export function ListPage() {
  const { type } = useParams<{ type: string }>();
  const [, setLocation] = useLocation();
  const meta = findObjectType(type);
  const [records, setRecords] = useState<ObjectRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  useEffect(() => {
    setRecords(null);
    setError(null);
    api
      .list(type, { limit: 25, ...(cursor ? { after: cursor } : {}) })
      .then((r) => {
        setRecords(r.results);
        setNextCursor(r.paging?.next?.after ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, [type, cursor]);

  if (!meta) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-semibold">Unknown object type</h1>
        <p className="text-text-muted">{type}</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <span>{meta.icon}</span> {meta.label_plural}
          </h1>
          <p className="text-text-muted text-sm">{records?.length ?? '…'} records</p>
        </div>
        <Link href={`/objects/${type}/new`} className="btn-primary">
          + New {meta.label}
        </Link>
      </header>

      {error && (
        <div className="card p-4 mb-4 border-danger text-danger">
          Error loading records: {error}
        </div>
      )}

      {records === null ? (
        <SkeletonTable />
      ) : records.length === 0 ? (
        <EmptyState meta={meta} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-panel-2">
                {meta.display_fields.map((f) => (
                  <th key={f} className="text-left text-xs uppercase tracking-wider text-text-muted px-4 py-2 font-medium">
                    {prettify(f)}
                  </th>
                ))}
                <th className="text-right text-xs uppercase tracking-wider text-text-muted px-4 py-2 font-medium">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setLocation(`/objects/${type}/${r.id}`)}
                  className="border-b border-border hover:bg-panel-2 cursor-pointer"
                >
                  {meta.display_fields.map((f) => (
                    <td key={f} className="px-4 py-2.5 text-sm">
                      {formatValue(r.properties[f]) || (
                        <span className="text-text-dim">—</span>
                      )}
                    </td>
                  ))}
                  <td className="text-right px-4 py-2.5 text-xs text-text-muted">
                    {timeAgo(r.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(cursor || nextCursor) && (
        <div className="flex justify-between mt-4">
          <button
            className="btn"
            disabled={!cursor}
            onClick={() => setCursor(undefined)}
          >
            ← First page
          </button>
          {nextCursor && (
            <button className="btn" onClick={() => setCursor(nextCursor)}>
              Next page →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="card p-4">
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-8 bg-panel-2 rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ meta }: { meta: ReturnType<typeof findObjectType> }) {
  if (!meta) return null;
  return (
    <div className="card p-12 text-center">
      <div className="text-5xl mb-4">{meta.icon}</div>
      <h2 className="text-lg font-medium mb-2">No {meta.label_plural.toLowerCase()} yet</h2>
      <p className="text-text-muted text-sm mb-6 max-w-md mx-auto">
        Create your first {meta.label.toLowerCase()} or import from a CSV — imports coming soon.
      </p>
      <Link href={`/objects/${meta.id}/new`} className="btn-primary">
        + Create {meta.label}
      </Link>
    </div>
  );
}

function prettify(field: string): string {
  return field
    .replace(/^hs_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
